import { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../config';
import { useAuth } from '../context/AuthContext';

export interface LynchClassification {
    symbole: string;
    categorie: string | null;
    categorie_titre?: string;
    categorie_emoji?: string;
    interpretation?: string | null;
    categorie_precedente?: string | null;
    date_dernier_changement?: string | null;
    pending_categorie?: string | null;
    is_manual_override?: boolean;
    criteres?: Record<string, unknown> | null;
    updated_at?: string | null;
}

/**
 * Classification Lynch (6 profils) d'une valeur — GET /v1/stocks/{symbole}/lynch-classification.
 * Réservé admin_platform côté backend (fonctionnalité en fiabilisation, pas encore
 * exposée aux clients Pro) : à n'appeler que depuis un écran déjà gated admin.
 */
export function useLynchClassification(symbole: string | null | undefined) {
    const { token } = useAuth();
    const [data, setData] = useState<LynchClassification | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchData = useCallback(async () => {
        if (!token || !symbole) return;
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_URL}/v1/stocks/${symbole}/lynch-classification`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.ok) {
                setData(await response.json());
            } else if (response.status !== 403) {
                setError('Erreur lors de la récupération de la classification Lynch.');
            }
        } catch (err) {
            console.error('Error fetching Lynch classification:', err);
            setError('Erreur réseau.');
        } finally {
            setLoading(false);
        }
    }, [token, symbole]);

    useEffect(() => {
        if (token && symbole) {
            fetchData();
        } else {
            setData(null);
            setLoading(false);
        }
    }, [token, symbole, fetchData]);

    return { data, loading, error, refetch: fetchData };
}
